export type Vec = { x: number; y: number };

export const clamp = (v: number, min: number, max: number) => Math.max(min, Math.min(max, v));

export const length = (v: Vec) => Math.hypot(v.x, v.y);
export const normalize = (v: Vec): Vec => {
  const len = length(v) || 1;
  return { x: v.x / len, y: v.y / len };
};

export const add = (a: Vec, b: Vec): Vec => ({ x: a.x + b.x, y: a.y + b.y });
export const sub = (a: Vec, b: Vec): Vec => ({ x: a.x - b.x, y: a.y - b.y });
export const mul = (v: Vec, s: number): Vec => ({ x: v.x * s, y: v.y * s });

export const rotatePoint = (cx: number, cy: number, px: number, py: number, deg: number): Vec => {
  const r = (deg || 0) * Math.PI / 180;
  const cos = Math.cos(r), sin = Math.sin(r);
  return { x: cx + px * cos - py * sin, y: cy + px * sin + py * cos };
};

export const invRotatePoint = (cx: number, cy: number, wx: number, wy: number, deg: number): Vec => {
  const r = -(deg || 0) * Math.PI / 180;
  const dx = wx - cx, dy = wy - cy;
  const cos = Math.cos(r), sin = Math.sin(r);
  return { x: dx * cos - dy * sin, y: dx * sin + dy * cos };
};

export const rotateVec = (vx: number, vy: number, deg: number): Vec => {
  const r = (deg || 0) * Math.PI / 180;
  const cos = Math.cos(r), sin = Math.sin(r);
  return { x: vx * cos - vy * sin, y: vx * sin + vy * cos };
};

export type Side = 'top' | 'right' | 'bottom' | 'left';

export const outwardForSide = (side: Side): Vec => {
  switch (side) {
    case 'top': return { x: 0, y: -1 };
    case 'right': return { x: 1, y: 0 };
    case 'bottom': return { x: 0, y: 1 };
    case 'left': return { x: -1, y: 0 };
  }
};

export const inwardForSide = (side: Side): Vec => {
  const v = outwardForSide(side);
  return { x: -v.x, y: -v.y };
};

export const nearestPointOnRotatedRectEdge = (
  center: Vec,
  width: number,
  height: number,
  rotation: number,
  worldPoint: Vec
): { point: Vec; side: Side } => {
  const local = invRotatePoint(center.x, center.y, worldPoint.x, worldPoint.y, rotation);
  const halfW = width / 2, halfH = height / 2;
  const clampedX = clamp(local.x, -halfW, halfW);
  const clampedY = clamp(local.y, -halfH, halfH);
  const dTop = Math.abs(-halfH - local.y);
  const dRight = Math.abs(halfW - local.x);
  const dBottom = Math.abs(halfH - local.y);
  const dLeft = Math.abs(-halfW - local.x);
  const minD = Math.min(dTop, dRight, dBottom, dLeft);
  let side: Side = 'top';
  let projLocal: Vec = { x: clampedX, y: -halfH };
  if (minD === dRight) { side = 'right'; projLocal = { x: halfW, y: clampedY }; }
  else if (minD === dBottom) { side = 'bottom'; projLocal = { x: clampedX, y: halfH }; }
  else if (minD === dLeft) { side = 'left'; projLocal = { x: -halfW, y: clampedY }; }
  const point = rotatePoint(center.x, center.y, projLocal.x, projLocal.y, rotation);
  return { point, side };
};


